import type { CSSProperties, FC, ReactNode } from 'react'
import type { MemoryPhotoData } from '@/types'

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'
import { cn } from '@/lib/utils'
import { getDisplayRatio } from '@/lib/photos'

interface MemoryPhotoProps {
    photo: MemoryPhotoData
    className?: string
    imageClassName?: string
    style?: CSSProperties
    ratio?: number
    loading?: 'eager' | 'lazy'
}

// Frames a memory photo and turns the ones stored sideways upright.
// If the browser already shows such a photo upright (landscape pixels), the turn is skipped.
const MemoryPhoto: FC<MemoryPhotoProps> = ({
    photo,
    className,
    imageClassName,
    style,
    ratio,
    loading = 'lazy'
}): ReactNode => {
    const imageRef = useRef<HTMLImageElement>(null)
    const [turned, setTurned] = useState(photo.rotate)

    const check = useCallback((image: HTMLImageElement): void => {
        if (photo.rotate && image.naturalWidth > image.naturalHeight) setTurned(false)
    }, [photo.rotate])

    useEffect(() => {
        const image = imageRef.current

        if (image?.complete && image.naturalWidth > 0) check(image)
    }, [check])

    return (
        <div
            className={cn('relative w-full overflow-hidden bg-muted [container-type:size]', className)}
            style={{ aspectRatio: ratio ?? getDisplayRatio(photo), ...style }}
        >
            <img
                ref={imageRef}
                src={photo.src}
                alt={photo.alt}
                loading={loading}
                decoding='async'
                draggable={false}
                onLoad={(event) => check(event.currentTarget)}
                style={turned ? undefined : { objectPosition: photo.position }}
                className={cn(
                    'absolute max-w-none select-none object-cover',
                    turned
                        ? 'left-1/2 top-1/2 h-[100cqw] w-[100cqh] -translate-x-1/2 -translate-y-1/2 rotate-90'
                        : 'inset-0 size-full',
                    imageClassName
                )}
            />
        </div>
    )
}

export default MemoryPhoto
